import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CreditCard,
  Crown,
  Loader2,
  ReceiptText,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";
import { SignInRequired } from "@/components/SignInRequired";
import { useAuth } from "@/hooks/useAuth";
import { getPaymentEnvironment } from "@/lib/stripe";
import { PREMIUM_BENEFITS, TRIAL_DAYS, trialProgress } from "@/lib/premium";
import { APPLE_DISCLOSURE, CANCEL_ANYTIME, TRIAL_CANCEL_NOTE } from "@/lib/trial";
import { createBillingPortalSession, restorePurchases } from "@/utils/payments.functions";
import { isNativeStoreApp, storeLabel } from "@/lib/store-billing";
import { openStoreManagement, restoreStorePurchases } from "@/lib/native-billing";
import { PremiumBadge } from "@/components/PremiumBadge";
import { TrialOfferCard } from "@/components/TrialOfferCard";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

export const Route = createFileRoute("/subscription")({
  component: SubscriptionPage,
  head: () => ({
    meta: [
      { title: "Manage Your Inquira Premium Subscription" },
      {
        name: "description",
        content:
          "View your Inquira Premium trial or subscription status, update payment details, restore purchases, or cancel anytime.",
      },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Manage Your Inquira Premium Subscription" },
      {
        property: "og:description",
        content: "Trial countdown, renewal date, billing history and cancellation — all in one place.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Manage Your Inquira Premium Subscription" },
      { name: "twitter:description", content: "Check your Premium status, billing, and renewal date." },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/subscription" }],
  }),
});

const fmtDate = (value: string | null | undefined) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function SubscriptionPage() {
  const { user, isPaid, premium, planPriceId, refreshPremium, signOut } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const environment = getPaymentEnvironment();
  const native = isNativeStoreApp();
  const storePlatform = premium?.billing_platform === "apple" || premium?.billing_platform === "google"
    ? premium.billing_platform
    : null;

  // Returning from Apple's subscription settings must re-read verified status.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshPremium();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [refreshPremium]);

  if (!user) {
    return <SignInRequired title="Manage your subscription" description="Sign in to view your Premium status and billing." />;
  }

  const trial = premium ? trialProgress(premium) : null;
  const onTrial = trial?.active ?? false;
  const planLabel =
    (planPriceId ?? premium?.plan_name) === "inquira_pro_yearly" ? "Premium Yearly" : "Premium Monthly";
  const graceEnds = premium?.grace_period_ends_at;
  const inGrace = !!graceEnds && new Date(graceEnds) > new Date();

  const statusLabel = onTrial
    ? "Premium Trial"
    : inGrace
      ? "Payment issue"
      : isPaid
        ? premium?.cancel_at_period_end
          ? "Subscription Canceled"
          : "Active"
        : "Free plan";

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      // Store-billed members must be managed by the platform that charged them.
      if (storePlatform || native) {
        const opened = await openStoreManagement();
        if (opened) return;
        toast.info(`Manage this subscription in your ${storeLabel(storePlatform ?? "apple")} account settings.`);
        return;
      }
      void track("billing_portal_opened", { surface: "subscription" });
      const result = await createBillingPortalSession({
        data: { environment, origin: window.location.origin },
      });
      if (!result?.url) {
        toast.error("No billing account found yet. Start Premium to manage payments.");
        return;
      }
      window.location.href = result.url;
    } catch (err) {
      console.error("Portal failed:", err);
      toast.error("Couldn't open the billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  const restore = async () => {
    setRestoring(true);
    try {
      if (storePlatform || native) {
        const store = await restoreStorePurchases();
        toast[store.premium ? "success" : "info"](
          store.premium
            ? "Purchases restored — Premium is active."
            : `Checked ${store.refreshed} store purchase(s). No active Premium found.`,
        );
        await refreshPremium();
        return;
      }
      const result = await restorePurchases({ data: { environment } });
      if (result?.error === "no_customer") {
        toast.info("No previous purchases found for this account.");
      } else if (result?.premium) {
        toast.success("Purchases restored — Premium is active.");
      } else {
        toast.info(`Checked ${result?.restored ?? 0} subscription(s). No active Premium found.`);
      }
      await refreshPremium();
    } catch (err) {
      console.error("Restore failed:", err);
      toast.error("Couldn't restore purchases. Please try again.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      <PaymentTestModeBanner />
      <AppHeader user={user} isPaid={isPaid} onSignOut={() => void signOut()} />

      <main className="mx-auto w-full max-w-2xl space-y-4 px-4 py-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink">
          <ArrowLeft className="size-4" /> Back
        </Link>

        <h1 className="font-[family-name:var(--font-display)] text-2xl font-extrabold text-ink">
          Your Subscription
        </h1>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <Link to="/premium-status" className="inline-block text-xs font-semibold text-neon-pink underline">
            Purchase date, period &amp; cancellation dates
          </Link>
          <Link to="/subscription-status" className="inline-block text-xs font-semibold text-ink-muted underline hover:text-ink">
            Subscription &amp; trial status (diagnostics)
          </Link>
        </div>


        {!isPaid && <TrialOfferCard state={premium} surface="subscription" />}

        <section className="rounded-2xl border border-rule bg-card p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex size-11 items-center justify-center rounded-full bg-gradient-to-br from-neon-pink to-neon-yellow">
                <Crown className="size-5 text-white" />
              </span>
              <div>
                <p className="font-[family-name:var(--font-display)] font-extrabold text-ink">
                  {isPaid ? planLabel : "Free plan"}
                </p>
                {isPaid && <PremiumBadge size="xs" className="mt-1" />}
                <p className="text-xs text-ink-muted">
                  {onTrial
                    ? `Day ${trial?.dayNumber} of ${trial?.daysTotal} · ${trial?.label}`
                    : isPaid
                      ? "$9.99 / month"
                      : "3 scans per day"}
                </p>
                {storePlatform && (
                  <p className="mt-1 text-[11px] text-ink-muted">
                    Billed through {storeLabel(storePlatform)}
                  </p>
                )}
              </div>
            </div>
            <span
              className={`rounded-full px-3 py-1 text-[11px] font-bold ${
                inGrace
                  ? "bg-destructive/15 text-destructive"
                  : isPaid
                    ? "bg-neon-yellow/20 text-ink"
                    : "bg-muted text-ink-muted"
              }`}
            >
              {statusLabel}
            </span>
          </div>

          {onTrial && (
            <div className="mt-4">
              <div
                className="h-2 w-full overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={trial?.percent ?? 0}
                aria-label="Trial progress"
              >
                <div
                  className="h-full rounded-full bg-gradient-to-r from-neon-pink to-neon-yellow"
                  style={{ width: `${Math.min(100, Math.max(4, trial?.percent ?? 0))}%` }}
                />
              </div>
              <p className="mt-2 text-xs font-semibold text-ink">
                Premium Trial Active · Trial ends:{" "}
                {fmtDate(premium?.store_expires_at ?? premium?.trial_ends_at)}
              </p>
              <p className="mt-1 text-xs text-ink-muted">
                Next billing amount: $9.99 · Next billing date:{" "}
                {fmtDate(premium?.store_expires_at ?? premium?.current_period_end ?? premium?.trial_ends_at)}. Your {TRIAL_DAYS}-day trial
                converts to Inquira Premium Monthly automatically. Cancel before the trial ends to avoid the
                first $9.99 monthly charge.
              </p>
              <p className="mt-2 text-xs font-bold text-ink">
                {CANCEL_ANYTIME} {TRIAL_CANCEL_NOTE}
              </p>
            </div>
          )}

          {isPaid && premium?.cancel_at_period_end && (
            <div className="mt-4 rounded-xl bg-muted/70 p-3 text-xs text-ink">
              <p className="font-bold">Subscription Canceled</p>
              <p className="mt-1">
                Premium access remains available until{" "}
                <strong>
                  {fmtDate(premium?.store_expires_at ?? premium?.current_period_end ?? premium?.subscription_expires_at)}
                </strong>. You
                will not be charged for another renewal unless you resubscribe.
              </p>
            </div>
          )}

          <Button
            variant="scan"
            size="full"
            className="mt-4 font-[family-name:var(--font-display)]"
            onClick={() => void openPortal()}
            disabled={portalLoading}
          >
            {portalLoading ? <Loader2 className="size-5 animate-spin" /> : <CreditCard className="size-5" />}
            Manage or Cancel Subscription
          </Button>
          <p className="mt-2 text-center text-[11px] text-ink-muted">
            {storePlatform || native
              ? `Apple manages this subscription — this opens your ${storeLabel(storePlatform ?? "apple")} subscription settings. ${CANCEL_ANYTIME}`
              : `Opens your secure billing portal, where you can cancel in one step. ${CANCEL_ANYTIME}`}
          </p>

          {inGrace && (
            <p className="mt-4 flex items-start gap-2 rounded-xl bg-destructive/10 p-3 text-xs text-ink">
              <AlertTriangle className="mt-0.5 size-4 shrink-0 text-destructive" />
              We couldn't process your last payment. You keep Premium until {fmtDate(graceEnds)} — update your
              payment method to avoid losing access.
            </p>
          )}

          <dl className="mt-4 grid gap-2 text-xs sm:grid-cols-2">
            <div className="rounded-xl bg-muted/60 p-3">
              <dt className="flex items-center gap-1 text-ink-muted">
                <CalendarClock className="size-3.5" /> Next renewal
              </dt>
              <dd className="mt-1 font-bold text-ink">
                {premium?.cancel_at_period_end
                  ? `Access ends ${fmtDate(premium?.store_expires_at ?? premium?.current_period_end ?? premium?.subscription_expires_at)}`
                  : fmtDate(premium?.store_expires_at ?? premium?.current_period_end ?? premium?.trial_ends_at)}
              </dd>
              {premium?.store_expires_at && (
                <dd className="mt-1 text-[11px] text-ink-muted">
                  Verified with {storeLabel(premium.store_platform as never)}
                </dd>
              )}
            </div>
            <div className="rounded-xl bg-muted/60 p-3">
              <dt className="flex items-center gap-1 text-ink-muted">
                <Sparkles className="size-3.5" /> Member since
              </dt>
              <dd className="mt-1 font-bold text-ink">
                {fmtDate(premium?.subscription_started_at ?? premium?.trial_started_at)}
              </dd>
            </div>
          </dl>
        </section>

        <section className="grid gap-2 sm:grid-cols-2">
          {!isPaid && (
            <Link to="/upgrade" className="sm:col-span-2">
              <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
                <Sparkles className="size-5" /> Get Premium — $9.99/mo
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => void openPortal()} disabled={portalLoading}>
            {portalLoading ? <Loader2 className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
            Update payment method
          </Button>
          <Button variant="outline" onClick={() => void restore()} disabled={restoring}>
            {restoring ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
            Restore purchases
          </Button>
          <Link to="/billing">
            <Button variant="outline" size="full">
              <ReceiptText className="size-4" /> Billing history
            </Button>
          </Link>
          {isPaid && !premium?.cancel_at_period_end && !storePlatform && !native && (
            <Button variant="outline" onClick={() => setShowCancel(true)}>
              Cancel subscription now
            </Button>
          )}
        </section>

        <section className="rounded-2xl border border-rule bg-card p-5">
          <h2 className="font-[family-name:var(--font-display)] text-sm font-extrabold text-ink">
            What Premium includes
          </h2>
          <ul className="mt-3 grid gap-1.5 text-sm text-ink-muted sm:grid-cols-2">
            {PREMIUM_BENEFITS.map((benefit) => (
              <li key={benefit}>• {benefit}</li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border border-rule bg-card p-5 text-[11px] leading-relaxed text-ink-muted">
          <p className="text-xs font-bold text-ink">{CANCEL_ANYTIME}</p>
          <p className="mt-1">
            Canceling stops future renewals; for a paid period you keep Premium until it ends. If you cancel
            during a free trial before it ends, you are not charged the first $9.99.
          </p>
          <p className="mt-2">{APPLE_DISCLOSURE}</p>
          <p className="mt-2 flex flex-wrap gap-3">
            <Link to="/terms" className="font-semibold text-neon-pink underline">Terms of Service</Link>
            <Link to="/privacy" className="font-semibold text-neon-pink underline">Privacy Policy</Link>
            <Link to="/support" className="font-semibold text-neon-pink underline">Help & Support</Link>
          </p>
        </section>
        <p className="pb-6 text-center text-[11px] text-ink-muted">Questions? info@inquira.online</p>
      </main>

      {showCancel && (
        <CancelSubscriptionDialog
          userId={user.id}
          planPriceId={planPriceId}
          onClose={() => setShowCancel(false)}
          onCanceled={() => {
            setShowCancel(false);
            void refreshPremium();
          }}
        />
      )}
    </div>
  );
}